import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { GripVertical, Images, Palette, Upload, X } from "lucide-react"
import { toast } from "sonner"
import { LibraryPickerDialog } from "@/components/library/library-picker-dialog"
import { StageAnchored } from "@/components/locations/stage-anchored"
import { SceneColorPicker } from "@/components/locations/scene-color-picker"
import { GenerateWithModelLabel, type GenerateWithModelLabelHandle } from "@/components/media/generate-with-model-label"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import {
  clampFloatingCardPos,
  FloatingDraggableCard,
  floatingCardParent,
  type FloatingCardEdgePads,
  type FloatingCardPos,
  type FloatingDraggableCardHandle,
} from "@/components/ui/floating-draggable-card"
import { Button, Input, Textarea } from "@/components/ui/primitives"
import { WorkspaceFullscreenPanel } from "@/components/workspace/workspace-fullscreen-panel"
import { useTranslation } from "@/hooks/use-translation"
import { usePlatformSelection } from "@/hooks/use-platform-selection"
import { confirm } from "@/lib/confirm"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { SCENE_3D_STAGE_ATTR } from "@/lib/dialog-layer"
import { SCENE_STAGE_EDGE_PADS } from "@/lib/location-scene-3d/place-in-bounds"
import {
  assignSceneFaceFromLibrary,
  clearSceneFace,
  generateSceneFace,
  remapSceneFaces,
  uploadSceneFace,
} from "@/lib/location-scene-3d-api"
import { emptyCameraPath } from "@/lib/location-scene-3d/camera-path"
import {
  applyHeightDelta,
  applyWallTileColors,
  applyOpeningDepthDelta,
  applyWallFaceCellShift,
  clampDigOpenings,
  DEFAULT_SECTOR_SIZE,
  emptyPlatform,
  SECTORS_MAX,
  SECTORS_MIN,
  OPENING_STEP,
  openingDigThrough,
  platformError,
  platformIsSet,
  remapSceneFacesForWallShift,
  scrubOpeningsForFaceKeys,
  sectorTopFaceKeys,
  selectionHasFloorCell,
  wallMediaFaceKeys,
  wallRunSiblingFaceKeys,
  wallSelectionTiles,
  type FaceTile,
  type PlatformUnit,
  type SceneCell,
  type ScenePlatform,
} from "@/lib/location-scene-3d/platform"
import {
  DEFAULT_CAMERA_STATE,
  emptySceneLayout,
  normalizeSceneLayout,
  normalizeSceneLight,
  type SceneFaceState,
  type SceneLayoutState,
  type SceneLightState,
} from "@/lib/location-scene-3d/scene-state"
import { REFERENCE_IMAGE_FILE_ACCEPT } from "@/lib/reference-file-policy"
import type { ShotFrameSource } from "@/lib/workspace-api"
import { lightPosition, type PlatformLightSide } from "@/lib/location-scene-3d/platform-layer"

/** Keep every face this request did not write. Server snapshots omit concurrent edits. */
function mergeTouchedFaces(
  current: ReadonlyArray<SceneFaceState> | null | undefined,
  returned: ReadonlyArray<SceneFaceState> | null | undefined,
  keys: ReadonlyArray<string>,
): SceneFaceState[] {
  const keySet = new Set(keys)
  const returnedByKey = new Map(
    (returned ?? []).filter((face) => keySet.has(face.face_key)).map((face) => [face.face_key, face]),
  )
  const byKey = new Map((current ?? []).map((face) => [face.face_key, face]))
  for (const key of keySet) {
    const next = returnedByKey.get(key)
    if (next) byKey.set(key, next)
    else byKey.delete(key)
  }
  return [...byKey.values()]
}

export type SelectionExtrudeApi = {
  visible: boolean
  kind: "sector" | "face"
  canExtrude: boolean
  canIntrude: boolean
  extrude: () => void
  intrude: () => void
  /** Sector selection only: live preview / persist solid color on top faces. */
  previewSolidColor?: (hex: string) => void
  commitSolidColor?: (hex: string) => Promise<void>
  clearSolidColor?: () => Promise<void>
}

export type LightPresetsApi = {
  preset: SceneLightState["preset"]
  apply: (side: PlatformLightSide) => void
}

export type PlatformFormApi = {
  draft: ScenePlatform
  setDraft: (patch: Partial<ScenePlatform>) => void
  apply: (patch?: Partial<ScenePlatform>) => void
  busy: boolean
  canEdit: boolean
}

/** Keep face prompt float below top toolbar and above bottom timeline. */
const FACE_FLOAT_EDGE_PADS: FloatingCardEdgePads = SCENE_STAGE_EDGE_PADS
const FACE_FLOAT_EST_W = 320
const FACE_FLOAT_EST_H = 180

function stageElement(): HTMLElement | null {
  const node = document.querySelector(`[${SCENE_3D_STAGE_ATTR}]`)
  return node instanceof HTMLElement ? node : null
}

function faceFloatPosNearAnchor(
  anchor: { x: number; y: number },
  cardW: number,
  cardH: number,
  parentW: number,
  parentH: number,
): FloatingCardPos {
  const gap = 8
  const arrowBarW = 72
  const ax = anchor.x
  const ay = anchor.y - 6
  let left = ax - gap - cardW
  if (left < gap) left = ax + arrowBarW + gap
  const top = ay - cardH
  return clampFloatingCardPos(left, top, parentW, parentH, cardW, cardH, FACE_FLOAT_EDGE_PADS)
}

type LocationScenePlatformDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  locationId: number | null
  locationName: string
  frameFormat?: string | null
  modelLabel?: string | null
  canEdit?: boolean
  /** Render the editor body without its own fullscreen shell. */
  embedded?: boolean
  /** Panels over the shared previz canvas. No second renderer. */
  hosted?: boolean
  /** Shared layout from LocationScene3d — skips own GET when hosted. */
  hostedLayout?: SceneLayoutState | null
  onLayoutApplied?: (layout: SceneLayoutState) => void
  /** Bound Scene3d id (draft or edit). */
  scene3dId?: number | null
  /** Create/bind a Scene3d draft when platform/face ops need an id. */
  ensureScene3dId?: () => Promise<number | null>
  /** @deprecated Use scene3dId. */
  previzId?: number | null
  /** @deprecated Use ensureScene3dId. */
  ensurePrevizId?: () => Promise<number | null>
  onBindInput?: (handlers: {
    sectors: (sectors: { x: number; z: number }[], additive: boolean, opts?: { prelude?: boolean }) => void
    face: (faceKey: string | null, additive?: boolean) => void
    faceTiles: (tiles: FaceTile[], additive: boolean) => void
    clear: () => void
  }) => void
  onToolChange?: (tool: "sector" | "surface" | null) => void
  onVisual?: (state: import("@/lib/location-scene-3d/platform-layer").PlatformLayerState) => void
  /** Live solid fill preview (no upload) — engine hot-patches materials. */
  onPatchSolidFaceColors?: (faceKeys: ReadonlyArray<string>, hex: string) => void
  /** Live wall-row color preview. `hex` null clears the selected rows. */
  onPatchWallTileColors?: (tiles: ReadonlyArray<FaceTile>, hex: string | null) => void
  /** Drop solid fill preview / reset floor color on selected tops. */
  onClearSolidFaceColors?: (faceKeys: ReadonlyArray<string>) => void
  onLightPersist?: (light: import("@/lib/location-scene-3d/scene-state").SceneLightState) => void
  onBindClearTool?: (clear: () => void) => void
  onBindActivateTool?: (activate: (tool: "sector" | "surface") => void) => void
  onBindSelectionExtrude?: (api: SelectionExtrudeApi | null) => void
  onBindLightPresets?: (api: LightPresetsApi | null) => void
  onBindPlatformForm?: (api: PlatformFormApi | null) => void
  /** Local platform mutation applied; parent handles history + optional network persist. */
  onPlatformCommit?: (args: {
    layout: SceneLayoutState
    historyLabelKey: string
    historyLabelParams?: Record<string, string | number>
  }) => void
  /** Screen anchor of face shift arrows (stage CSS px); used to park the prompt card opposite them. */
  faceToolbarAnchor?: { x: number; y: number } | null
}

export function LocationScenePlatformDialog({
  open,
  onOpenChange,
  projectId,
  locationId,
  locationName,
  frameFormat,
  modelLabel,
  canEdit = true,
  embedded = false,
  hosted = false,
  hostedLayout = null,
  onLayoutApplied,
  scene3dId = null,
  ensureScene3dId,
  previzId = null,
  ensurePrevizId,
  onBindInput,
  onToolChange,
  onVisual,
  onPatchSolidFaceColors,
  onPatchWallTileColors,
  onClearSolidFaceColors,
  onLightPersist,
  onBindClearTool,
  onBindActivateTool,
  onBindSelectionExtrude,
  onBindLightPresets,
  onBindPlatformForm,
  onPlatformCommit,
  faceToolbarAnchor = null,
}: LocationScenePlatformDialogProps) {
  const { t } = useTranslation(["workspace"])
  const [layout, setLayout] = useState<SceneLayoutState | null>(null)
  const layoutRef = useRef<SceneLayoutState | null>(null)
  layoutRef.current = layout
  const [draft, setDraft] = useState<ScenePlatform>(emptyPlatform())
  const [light, setLight] = useState<SceneLightState>(() => normalizeSceneLight(null))
  const [prompt, setPrompt] = useState("")
  const [busy, setBusy] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [faceSketchOpen, setFaceSketchOpen] = useState(false)
  const [wallColor, setWallColor] = useState("#c8c8c8")
  const [wallColorOpen, setWallColorOpen] = useState(false)
  const wallColorDirtyRef = useRef(false)
  const wallColorPreviewRafRef = useRef<number | null>(null)
  const wallColorPendingHexRef = useRef<string | null>(null)
  const wallColorPopoverRef = useRef<HTMLDivElement>(null)
  const wallColorPanelRef = useRef<HTMLDivElement>(null)
  const faceUploadInputRef = useRef<HTMLInputElement>(null)

  const applyLayout = useCallback(
    (next: SceneLayoutState) => {
      layoutRef.current = next
      setLayout(next)
      if (platformIsSet(next.platform)) setDraft(next.platform)
      if (next.light) setLight(normalizeSceneLight(next.light))
      onLayoutApplied?.(next)
    },
    [onLayoutApplied],
  )

  const applyTouchedFaces = useCallback(
    (returned: SceneLayoutState, keys: ReadonlyArray<string>, historyLabelKey: string) => {
      const current = layoutRef.current
      const next = normalizeSceneLayout({
        ...emptySceneLayout(),
        ...(current ?? returned),
        exists: true,
        faces: mergeTouchedFaces(current?.faces ?? returned.faces, returned.faces, keys),
      })
      applyLayout(next)
      onPlatformCommit?.({ layout: next, historyLabelKey })
    },
    [applyLayout, onPlatformCommit],
  )

  const patchLayoutWallTiles = useCallback((tiles: ReadonlyArray<FaceTile>, hex: string | null) => {
    setLayout((prev) => {
      if (!prev || !platformIsSet(prev.platform)) return prev
      const next = {
        ...prev,
        faces: applyWallTileColors(prev.faces ?? [], tiles, hex, prev.cells ?? [], prev.platform),
      }
      layoutRef.current = next
      return next
    })
  }, [])

  const patchLayoutFaceColors = useCallback((faceKeys: ReadonlyArray<string>, hex: string | null) => {
    setLayout((prev) => {
      if (!prev) return prev
      const keySet = new Set(faceKeys)
      let next: SceneLayoutState
      if (hex == null) {
        next = {
          ...prev,
          faces: (prev.faces ?? []).map((face) => (keySet.has(face.face_key) ? { ...face, solid_color: null } : face)),
        }
      } else {
        const nextFaces = (prev.faces ?? []).map((face) =>
          keySet.has(face.face_key) ? { ...face, solid_color: hex, status: "ready" as const } : face,
        )
        for (const faceKey of faceKeys) {
          if (nextFaces.some((face) => face.face_key === faceKey)) continue
          nextFaces.push({
            face_key: faceKey,
            origin: "upload",
            status: "ready",
            solid_color: hex,
          })
        }
        next = { ...prev, faces: nextFaces }
      }
      layoutRef.current = next
      return next
    })
  }, [])

  useEffect(() => {
    if (!hosted || !hostedLayout) return
    layoutRef.current = hostedLayout
    setLayout(hostedLayout)
    if (platformIsSet(hostedLayout.platform)) setDraft(hostedLayout.platform)
    if (hostedLayout.light) setLight(normalizeSceneLight(hostedLayout.light))
  }, [hosted, hostedLayout])

  const faces = layout?.faces ?? []
  const platform = platformIsSet(layout?.platform) ? layout.platform : null
  const cells = useMemo(() => layout?.cells ?? [], [layout?.cells])
  const {
    selectedSectors,
    selectedFaceTiles,
    surfaceSelect,
    surfaceGrid,
    mode,
    panelOpen: surfacePanelOpen,
    clear: clearSelection,
    handlers: selectionHandlers,
    replaceFaceTiles,
    filterFaceTiles,
  } = usePlatformSelection({ platform, cells })

  const selectedFaceKeys = [...new Set(selectedFaceTiles.map((tile) => tile.faceKey))]
  const selectedFaceKey = selectedFaceKeys[0] ?? null
  const selectedFaces = faces.filter((face) => selectedFaceKeys.includes(face.face_key))
  const primaryFace = selectedFaces[0] ?? null
  const delayEntityId = scene3dId ?? previzId ?? locationId ?? 0
  /** Face media ops hit Scene3d API (need scene3d id + server blobs). */
  const resolvePrevizIdForFaces = useCallback(async () => {
    const id =
      scene3dId ??
      previzId ??
      (ensureScene3dId ? await ensureScene3dId() : null) ??
      (ensurePrevizId ? await ensurePrevizId() : null)
    if (id == null) throw new Error("Scene3d required")
    return id
  }, [ensurePrevizId, ensureScene3dId, previzId, scene3dId])

  const commitLocal = useCallback(
    (
      patch: {
        platform?: ScenePlatform | null
        cells?: SceneCell[]
        openings?: SceneLayoutState["openings"]
        light?: SceneLightState
        faces?: SceneFaceState[]
      },
      historyLabelKey: string,
      historyLabelParams?: Record<string, string | number>,
    ) => {
      const current = layout
      const next = normalizeSceneLayout({
        ...emptySceneLayout(),
        ...current,
        exists: true,
        objects: current?.objects ?? [],
        camera_state: current?.camera_state ?? DEFAULT_CAMERA_STATE,
        start_camera: current?.start_camera ?? null,
        camera_path: current?.camera_path ?? emptyCameraPath(),
        platform: patch.platform !== undefined ? patch.platform : (current?.platform ?? null),
        cells: patch.cells ?? current?.cells ?? [],
        openings: patch.openings ?? current?.openings ?? [],
        light: patch.light ?? current?.light ?? light,
        faces: patch.faces ?? current?.faces ?? [],
      })
      applyLayout(next)
      onPlatformCommit?.({ layout: next, historyLabelKey, historyLabelParams })
    },
    [applyLayout, layout, light, onPlatformCommit],
  )

  const persistLight = useCallback(
    (next: SceneLightState) => {
      setLight(next)
      onLightPersist?.(next)
      if (!platform) return
      commitLocal({ platform, cells, light: next }, "locationsPage.scene3d.historyActions.light")
    },
    [cells, commitLocal, onLightPersist, platform],
  )

  const applyLightPreset = useCallback(
    (side: PlatformLightSide) => {
      const span = platform
        ? Math.max(platform.sectors_x, platform.sectors_z) *
          (platform.unit === "cm" ? platform.sector_size / 100 : platform.sector_size)
        : 8
      const next: SceneLightState = { preset: side, position: lightPosition(side, span) }
      if (typeof light.intensity === "number" && Number.isFinite(light.intensity) && light.intensity > 0) {
        next.intensity = light.intensity
      }
      persistLight(next)
    },
    [light.intensity, persistLight, platform],
  )

  const applyPlatform = useCallback(
    (patch?: Partial<ScenePlatform>) => {
      const nextDraft = { ...draft, ...patch, sector_size: DEFAULT_SECTOR_SIZE }
      const error = platformError(nextDraft)
      if (error) {
        toast.error(t(`locationsPage.scenePlatform.errors.${error}`))
        return
      }
      setDraft(nextDraft)
      if (mode === "sector") clearSelection()
      commitLocal({ platform: nextDraft, cells }, "locationsPage.scene3d.historyActions.platform")
    },
    [cells, clearSelection, commitLocal, draft, mode, t],
  )

  const patchPlatformDraft = useCallback((patch: Partial<ScenePlatform>) => {
    setDraft((prev) => ({ ...prev, ...patch, sector_size: DEFAULT_SECTOR_SIZE }))
  }, [])

  const extrude = useCallback(
    (direction: 1 | -1) => {
      if (!platform || selectedSectors.length === 0) return
      if (direction < 0 && selectionHasFloorCell(cells, selectedSectors)) return
      const nextCells = applyHeightDelta(cells, selectedSectors, direction)
      const count = selectedSectors.length
      commitLocal(
        { platform, cells: nextCells },
        direction > 0
          ? "locationsPage.scene3d.historyActions.raiseSectors"
          : "locationsPage.scene3d.historyActions.lowerSectors",
        { count },
      )
    },
    [cells, commitLocal, platform, selectedSectors],
  )

  const extrudeOpenings = useCallback(
    async (direction: 1 | -1) => {
      if (selectedFaceTiles.length === 0 || !platform) return

      // Wall mode: add/remove sector columns. Tiles mode keeps openings dig/paint.
      if (surfaceSelect === "wall") {
        const preShiftLayout = layout
        const shifted = applyWallFaceCellShift(cells, platform, selectedFaceTiles, direction)
        const openings = scrubOpeningsForFaceKeys(layout?.openings ?? [], shifted.affectedFaceKeys)
        const { faces: nextFaces, mappings } = remapSceneFacesForWallShift(
          layout?.faces ?? [],
          shifted.keyMap,
          shifted.orphanFaceKeys,
          shifted.cells,
          platform,
        )
        const nextTiles =
          shifted.nextFaceKeys.length > 0
            ? shifted.nextFaceKeys.flatMap((key) => wallSelectionTiles(key, shifted.cells, platform))
            : []
        // Dedupe tiles if multiple nextFaceKeys expand the same run.
        const seen = new Set<string>()
        const uniqueTiles = nextTiles.filter((tile) => {
          const id = `${tile.faceKey}:${tile.u}:${tile.v}`
          if (seen.has(id)) return false
          seen.add(id)
          return true
        })
        replaceFaceTiles(uniqueTiles)
        const count = selectedFaceTiles.length
        const historyLabelKey =
          direction < 0
            ? "locationsPage.scene3d.historyActions.shiftFacesOut"
            : "locationsPage.scene3d.historyActions.shiftFacesIn"
        commitLocal({ platform, cells: shifted.cells, openings, faces: nextFaces }, historyLabelKey, { count })
        const hadMaterial = new Set((layout?.faces ?? []).map((face) => face.face_key))
        const clearKeys = shifted.orphanFaceKeys.filter((key) => hadMaterial.has(key))
        const apiMappings = mappings.map((item) => ({
          from_face_key: item.from,
          to_face_key: item.to,
        }))
        if (apiMappings.length > 0 || clearKeys.length > 0) {
          try {
            const id = await resolvePrevizIdForFaces()
            await remapSceneFaces(projectId, id, apiMappings, clearKeys)
          } catch (error: unknown) {
            toast.error(error instanceof Error ? error.message : String(error))
            if (preShiftLayout) {
              applyLayout(preShiftLayout)
              onPlatformCommit?.({
                layout: preShiftLayout,
                historyLabelKey,
                historyLabelParams: { count },
              })
            }
          }
        }
        return
      }

      let next = layout?.openings ?? []
      const byFace = new Map<string, { u: number; v: number }[]>()
      for (const tile of selectedFaceTiles) {
        const list = byFace.get(tile.faceKey) ?? []
        list.push({ u: tile.u, v: tile.v })
        byFace.set(tile.faceKey, list)
      }
      for (const [faceKey, cellsForFace] of byFace) {
        next = applyOpeningDepthDelta(next, faceKey, cellsForFace, direction * OPENING_STEP)
      }
      next = clampDigOpenings(next, cells, platform)
      filterFaceTiles((tile) => {
        const opening = next.find((item) => item.face_key === tile.faceKey && item.u === tile.u && item.v === tile.v)
        if (!opening || opening.depth <= 0) return true
        return !openingDigThrough(opening, cells, platform)
      })
      const count = selectedFaceTiles.length
      commitLocal(
        { platform, cells, openings: next },
        direction < 0
          ? "locationsPage.scene3d.historyActions.shiftFacesOut"
          : "locationsPage.scene3d.historyActions.shiftFacesIn",
        { count },
      )
    },
    [
      applyLayout,
      cells,
      commitLocal,
      filterFaceTiles,
      layout,
      onPlatformCommit,
      platform,
      projectId,
      replaceFaceTiles,
      resolvePrevizIdForFaces,
      selectedFaceTiles,
      surfaceSelect,
    ],
  )

  /** Generate/upload/library targets: one primary per wall run in wall mode. */
  const mediaFaceKeys = useMemo(() => {
    if (!platform || selectedFaceKeys.length === 0) return selectedFaceKeys
    return wallMediaFaceKeys(selectedFaceKeys, cells, platform, surfaceSelect)
  }, [cells, platform, selectedFaceKeys, surfaceSelect])

  const runFaceKeysWrite = async (
    faceKeys: ReadonlyArray<string>,
    historyLabelKey: string,
    action: (faceKey: string, id: number) => Promise<SceneLayoutState>,
    opts?: { clearWallSiblings?: boolean },
  ) => {
    if (faceKeys.length === 0) return
    setBusy(true)
    try {
      const id = await resolvePrevizIdForFaces()
      let last: SceneLayoutState | null = null
      const touched = new Set(faceKeys)
      for (const faceKey of faceKeys) {
        last = normalizeSceneLayout(await action(faceKey, id))
        if (opts?.clearWallSiblings && surfaceSelect === "wall" && platform) {
          for (const sibling of wallRunSiblingFaceKeys(faceKey, cells, platform)) {
            const stillThere = (last.faces ?? []).some((face) => face.face_key === sibling)
            if (!stillThere) continue
            touched.add(sibling)
            last = normalizeSceneLayout(await clearSceneFace(projectId, id, sibling))
          }
        }
      }
      if (last) applyTouchedFaces(last, [...touched], historyLabelKey)
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    } finally {
      setBusy(false)
    }
  }

  const persistPaintedLayout = useCallback(
    (historyLabelKey: string) => {
      const current = layoutRef.current
      if (!current) return
      onPlatformCommit?.({ layout: current, historyLabelKey })
    },
    [onPlatformCommit],
  )

  const runWallMediaWrite = async (
    historyLabelKey: string,
    action: (faceKey: string, id: number) => Promise<SceneLayoutState>,
  ) => {
    await runFaceKeysWrite(mediaFaceKeys, historyLabelKey, action, { clearWallSiblings: true })
  }

  useEffect(() => {
    onToolChange?.(open ? mode : null)
  }, [mode, onToolChange, open])

  useEffect(() => {
    onBindClearTool?.(() => {
      clearSelection()
    })
  }, [clearSelection, onBindClearTool])

  useEffect(() => {
    // Selection FSM owns tool mode; keep bind so parent wiring stays stable.
    onBindActivateTool?.((_tool) => {})
  }, [onBindActivateTool])

  useEffect(() => {
    if (!onBindSelectionExtrude) return
    const sectorActive = mode === "sector" && selectedSectors.length > 0
    const faceActive = mode === "surface" && selectedFaceTiles.length > 0
    const visible = Boolean(canEdit && (sectorActive || faceActive))
    if (!visible) {
      onBindSelectionExtrude(null)
      return
    }
    if (sectorActive) {
      const floorFaceKeys = sectorTopFaceKeys(selectedSectors)
      onBindSelectionExtrude({
        visible: true,
        kind: "sector",
        canExtrude: true,
        canIntrude: !selectionHasFloorCell(cells, selectedSectors),
        extrude: () => void extrude(1),
        intrude: () => void extrude(-1),
        previewSolidColor: (hex) => {
          if (floorFaceKeys.length === 0) return
          patchLayoutFaceColors(floorFaceKeys, hex)
          onPatchSolidFaceColors?.(floorFaceKeys, hex)
        },
        commitSolidColor: async (hex) => {
          patchLayoutFaceColors(floorFaceKeys, hex)
          onPatchSolidFaceColors?.(floorFaceKeys, hex)
          persistPaintedLayout("locationsPage.scene3d.historyActions.faceColor")
        },
        clearSolidColor: async () => {
          patchLayoutFaceColors(floorFaceKeys, null)
          onClearSolidFaceColors?.(floorFaceKeys)
          persistPaintedLayout("locationsPage.scene3d.historyActions.faceClear")
        },
      })
    } else {
      onBindSelectionExtrude({
        visible: true,
        kind: "face",
        canExtrude: true,
        canIntrude: true,
        extrude: () => void extrudeOpenings(-1),
        intrude: () => void extrudeOpenings(1),
      })
    }
  }, [
    canEdit,
    cells,
    extrude,
    extrudeOpenings,
    mode,
    onBindSelectionExtrude,
    onClearSolidFaceColors,
    onPatchSolidFaceColors,
    patchLayoutFaceColors,
    persistPaintedLayout,
    selectedFaceTiles,
    selectedSectors,
  ])

  useEffect(() => {
    return () => onBindSelectionExtrude?.(null)
  }, [onBindSelectionExtrude])

  useEffect(() => {
    if (!onBindLightPresets) return
    onBindLightPresets({
      preset: light.preset,
      apply: applyLightPreset,
    })
  }, [applyLightPreset, light.preset, onBindLightPresets])

  useEffect(() => {
    return () => onBindLightPresets?.(null)
  }, [onBindLightPresets])

  useEffect(() => {
    if (!onBindPlatformForm) return
    onBindPlatformForm({
      draft,
      setDraft: patchPlatformDraft,
      apply: applyPlatform,
      busy,
      canEdit,
    })
  }, [applyPlatform, busy, canEdit, draft, onBindPlatformForm, patchPlatformDraft])

  useEffect(() => {
    return () => onBindPlatformForm?.(null)
  }, [onBindPlatformForm])

  useEffect(() => {
    onVisual?.({
      platform,
      cells: layout?.cells ?? [],
      faces: layout?.faces ?? [],
      openings: layout?.openings ?? [],
      selectedSectors,
      selectedFaceTiles,
      activeFaceKey: mode === "surface" ? selectedFaceKey : null,
      light,
      surfaceGrid,
    })
  }, [layout, light, mode, onVisual, platform, selectedFaceKey, selectedFaceTiles, selectedSectors, surfaceGrid])

  useEffect(() => {
    onBindInput?.(selectionHandlers)
  }, [onBindInput, selectionHandlers])

  const panelClass = hosted
    ? "pointer-events-auto absolute bottom-14 left-2 z-20 flex max-h-[calc(100%-1rem-3.5rem)] w-64 flex-col gap-3 overflow-y-auto rounded-lg bg-black/55 p-3 shadow"
    : "flex min-h-0 flex-col gap-3 overflow-y-auto"
  const showFacePanel = mode === "surface" && selectedFaceKeys.length > 0
  useEffect(() => {
    if (!showFacePanel) setPrompt("")
  }, [showFacePanel])

  useEffect(() => {
    if (!showFacePanel) {
      if (wallColorOpen && wallColorDirtyRef.current) {
        void commitWallColor(wallColorPendingHexRef.current ?? wallColor)
      }
      setWallColorOpen(false)
    }
  }, [showFacePanel])

  useEffect(() => {
    if (!wallColorOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      const inButton = wallColorPopoverRef.current?.contains(target) === true
      const inPanel = wallColorPanelRef.current?.contains(target) === true
      if (!inButton && !inPanel) closeWallColorPopover()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeWallColorPopover()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [wallColorOpen, wallColor])

  // Hosted: keep chrome during marquee prelude; hide only on explicit deselect.
  const showHostedPanel = hosted && mode === "surface" && (showFacePanel || surfacePanelOpen)
  const showStandaloneForm = !hosted
  const faceThumbSrc = primaryFace?.thumb_url || primaryFace?.url || primaryFace?.download_url || null
  const hasFaceImage = Boolean(faceThumbSrc)
  const showFaceThumb = hasFaceImage && !primaryFace?.solid_color
  const showFaceMediaRow = showHostedPanel && showFacePanel && (canEdit || hasFaceImage)

  useEffect(() => {
    const solid = primaryFace?.solid_color
    if (solid) setWallColor(solid)
  }, [primaryFace?.solid_color])
  const faceFloatCardRef = useRef<FloatingDraggableCardHandle | null>(null)
  const [faceMeasuredPos, setFaceMeasuredPos] = useState<FloatingCardPos | null>(null)
  const facePanelResetKey = showFacePanel ? "face-open" : "face-closed"

  /** Seed before mount so the card never paints at unsetStyle bottom-left. */
  const faceEstimatedPos = useMemo(() => {
    if (!hosted || !showFacePanel || !faceToolbarAnchor) return null
    const stage = stageElement()
    const parentW = stage?.clientWidth ?? 0
    const parentH = stage?.clientHeight ?? 0
    if (parentW <= 0 || parentH <= 0) return null
    return faceFloatPosNearAnchor(faceToolbarAnchor, FACE_FLOAT_EST_W, FACE_FLOAT_EST_H, parentW, parentH)
  }, [hosted, showFacePanel, faceToolbarAnchor])

  const faceDefaultPos = faceMeasuredPos ?? faceEstimatedPos

  useLayoutEffect(() => {
    if (!hosted || !showFacePanel || !faceToolbarAnchor) {
      setFaceMeasuredPos(null)
      return
    }
    const panel = faceFloatCardRef.current?.panel() ?? null
    const parent = floatingCardParent(panel) ?? stageElement()
    const w = panel?.offsetWidth || FACE_FLOAT_EST_W
    const h = panel?.offsetHeight || FACE_FLOAT_EST_H
    const parentW = parent?.clientWidth ?? 0
    const parentH = parent?.clientHeight ?? 0
    if (parentW <= 0 || parentH <= 0) return

    const next = faceFloatPosNearAnchor(faceToolbarAnchor, w, h, parentW, parentH)
    setFaceMeasuredPos((prev) => (prev && prev.left === next.left && prev.top === next.top ? prev : next))
  }, [hosted, showFacePanel, faceToolbarAnchor])

  const clearFaceMedia = async () => {
    if (busy || selectedFaceKeys.length === 0) return
    const clearLabel = t("locationsPage.scenePlatform.clear")
    if (
      !(await confirm({
        message: t("assetMedia.clearVersionConfirm"),
        confirmLabel: clearLabel,
        tone: "danger",
      }))
    ) {
      return
    }
    await runWallMediaWrite("locationsPage.scene3d.historyActions.faceClear", (faceKey, id) =>
      clearSceneFace(projectId, id, faceKey),
    )
  }

  const commitWallColor = (hex: string, opts?: { closePopover?: boolean }) => {
    setWallColor(hex)
    wallColorDirtyRef.current = false
    if (opts?.closePopover) setWallColorOpen(false)
    patchLayoutWallTiles(selectedFaceTiles, hex)
    onPatchWallTileColors?.(selectedFaceTiles, hex)
    persistPaintedLayout("locationsPage.scene3d.historyActions.faceColor")
  }

  const flushWallColorPreview = (hex: string) => {
    setWallColor(hex)
    wallColorDirtyRef.current = true
    if (selectedFaceTiles.length === 0) return
    patchLayoutWallTiles(selectedFaceTiles, hex)
    onPatchWallTileColors?.(selectedFaceTiles, hex)
  }

  const previewWallColor = (hex: string) => {
    wallColorPendingHexRef.current = hex
    setWallColor(hex)
    if (wallColorPreviewRafRef.current != null) return
    wallColorPreviewRafRef.current = requestAnimationFrame(() => {
      wallColorPreviewRafRef.current = null
      const pending = wallColorPendingHexRef.current
      if (pending) flushWallColorPreview(pending)
    })
  }

  const clearWallColor = () => {
    if (wallColorPreviewRafRef.current != null) {
      cancelAnimationFrame(wallColorPreviewRafRef.current)
      wallColorPreviewRafRef.current = null
    }
    wallColorPendingHexRef.current = null
    wallColorDirtyRef.current = false
    setWallColorOpen(false)
    patchLayoutWallTiles(selectedFaceTiles, null)
    onPatchWallTileColors?.(selectedFaceTiles, null)
    persistPaintedLayout("locationsPage.scene3d.historyActions.faceClear")
  }

  const closeWallColorPopover = () => {
    if (!wallColorOpen) return
    const hex = wallColorPendingHexRef.current ?? wallColor
    setWallColorOpen(false)
    if (wallColorDirtyRef.current) void commitWallColor(hex)
  }

  useEffect(() => {
    return () => {
      if (wallColorPreviewRafRef.current != null) cancelAnimationFrame(wallColorPreviewRafRef.current)
    }
  }, [])

  const faceMediaChrome = showFaceMediaRow ? (
    <div className="flex flex-wrap items-center gap-1.5">
      {showFaceThumb ? (
        <div className="relative h-7 w-7 shrink-0">
          <button
            type="button"
            className="h-full w-full overflow-hidden rounded-md border border-ui-border bg-ui-surface-sunken"
            title={t("locationsPage.scenePlatform.faceTitle")}
            aria-label={t("locationsPage.scenePlatform.faceTitle")}
            onClick={() => setFaceSketchOpen(true)}
          >
            <img src={faceThumbSrc!} alt="" className="h-full w-full object-cover" />
          </button>
          {canEdit ? (
            <button
              type="button"
              disabled={busy}
              className="absolute -top-1.5 -right-1.5 z-10 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full bg-[var(--ui-chip-bg)] text-ui-text-secondary transition-colors hover:bg-red-600/80 hover:text-ui-on-accent disabled:cursor-not-allowed disabled:opacity-50"
              title={t("locationsPage.scenePlatform.clear")}
              aria-label={t("locationsPage.scenePlatform.clear")}
              onClick={(e) => {
                e.stopPropagation()
                void clearFaceMedia()
              }}
            >
              <X className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
      {canEdit ? (
        <>
          <input
            ref={faceUploadInputRef}
            type="file"
            accept={REFERENCE_IMAGE_FILE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ""
              if (file) {
                void runWallMediaWrite("locationsPage.scene3d.historyActions.faceUpload", (faceKey, id) =>
                  uploadSceneFace(projectId, id, faceKey, file),
                )
              }
            }}
          />
          <Button
            type="button"
            size="xs"
            variant="secondary"
            disabled={busy}
            className="disabled:opacity-100"
            title={t("locationsPage.scenePlatform.upload")}
            aria-label={t("locationsPage.scenePlatform.upload")}
            onClick={() => faceUploadInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </Button>
          <Button
            type="button"
            size="xs"
            variant="secondary"
            disabled={busy}
            className="disabled:opacity-100"
            title={t("locationsPage.scenePlatform.library")}
            aria-label={t("locationsPage.scenePlatform.library")}
            onClick={() => setLibraryOpen(true)}
          >
            <Images className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </Button>
          <div className="flex h-7 shrink-0 items-center self-center" ref={wallColorPopoverRef}>
            <Button
              type="button"
              size="xs"
              variant="secondary"
              disabled={busy}
              className="disabled:opacity-100"
              title={t("locationsPage.scenePlatform.wallColor")}
              aria-label={t("locationsPage.scenePlatform.wallColor")}
              aria-expanded={wallColorOpen}
              onClick={() => {
                if (wallColorOpen) closeWallColorPopover()
                else setWallColorOpen(true)
              }}
            >
              <Palette className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Button>
            <StageAnchored
              open={wallColorOpen}
              anchorRef={wallColorPopoverRef}
              prefer="down"
              panelRef={wallColorPanelRef}
              className="z-50 w-max"
            >
              <SceneColorPicker
                value={wallColor}
                disabled={busy}
                onChange={previewWallColor}
                onPreset={(hex) => {
                  previewWallColor(hex)
                  void commitWallColor(hex, { closePopover: true })
                }}
                onClear={clearWallColor}
                labels={{
                  color: t("locationsPage.scenePlatform.wallColor"),
                  presetGreen: t("locationsPage.scenePlatform.wallColorPresetGreen"),
                  clearColor: t("locationsPage.scenePlatform.wallColorClear"),
                }}
              />
            </StageAnchored>
          </div>
        </>
      ) : null}
    </div>
  ) : null

  const facePromptPanel = showFacePanel ? (
    <FacePanel
      faces={selectedFaces}
      faceKeys={selectedFaceKeys}
      prompt={prompt}
      canEdit={canEdit}
      busy={busy}
      modelLabel={modelLabel}
      delayEntityId={delayEntityId}
      onPrompt={setPrompt}
      onGenerate={() =>
        runWallMediaWrite("locationsPage.scene3d.historyActions.faceGenerate", (faceKey, id) =>
          generateSceneFace(projectId, id, faceKey, prompt),
        )
      }
    />
  ) : null

  const facePanelBody = (
    <>
      {faceMediaChrome}
      {facePromptPanel}
    </>
  )

  const panels = hosted ? (
    showFacePanel && faceToolbarAnchor && faceDefaultPos ? (
      <FloatingDraggableCard
        cardRef={faceFloatCardRef}
        resetKey={facePanelResetKey}
        defaultPos={faceDefaultPos}
        edgePads={FACE_FLOAT_EDGE_PADS}
        className="pointer-events-auto z-20 flex max-h-[calc(100%-1rem-3.5rem)] w-80 flex-col gap-3 overflow-visible rounded-lg bg-black/55 p-3 shadow"
        aria-label={t("locationsPage.scenePlatform.faceTitle")}
        header={<GripVertical className="h-3.5 w-3.5 shrink-0 text-white/50" aria-hidden />}
        headerEnd={faceMediaChrome}
      >
        <div className="min-h-0 flex-1 overflow-y-auto">{facePromptPanel}</div>
      </FloatingDraggableCard>
    ) : null
  ) : showStandaloneForm ? (
    <div className={panelClass}>
      <PlatformForm
        draft={draft}
        canEdit={canEdit}
        busy={busy}
        onChange={setDraft}
        onApply={() => void applyPlatform()}
      />
      {facePanelBody}
    </div>
  ) : null
  const body = (
    <>
      {open ? panels : null}
      <LibraryPickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        projectId={projectId}
        frameFormat={frameFormat || "16:9"}
        targets={[{ id: "face", label: t("canvas.referencesModal.pickFromLibrary"), accepts: ["image"] }]}
        defaultTargetId="face"
        onPick={(_targetId, source: ShotFrameSource) => {
          if (mediaFaceKeys.length === 0) return
          setLibraryOpen(false)
          void runWallMediaWrite("locationsPage.scene3d.historyActions.faceLibrary", (faceKey, id) =>
            assignSceneFaceFromLibrary(projectId, id, faceKey, {
              entity_type: source.entity_type,
              entity_id: source.entity_id,
              state_id: source.state_id,
            }),
          )
        }}
      />
      <SketchDialog
        open={faceSketchOpen}
        onOpenChange={setFaceSketchOpen}
        projectId={projectId}
        sourceUrl={primaryFace?.download_url ?? primaryFace?.url ?? null}
        presentation="view"
        allowEdit={false}
        persist="none"
        nested
      />
    </>
  )
  if (hosted || embedded) return body
  return (
    <WorkspaceFullscreenPanel
      open={open}
      onOpenChange={onOpenChange}
      title={t("locationsPage.scenePlatform.title", { name: locationName })}
      description={t("locationsPage.scenePlatform.description")}
      closeAriaLabel={t("locationsPage.scenePlatform.close")}
      bodyClassName="min-h-0 overflow-hidden px-5 pt-1 pb-4"
    >
      {body}
    </WorkspaceFullscreenPanel>
  )
}

function PlatformForm({
  draft,
  canEdit,
  busy,
  onChange,
  onApply,
}: {
  draft: ScenePlatform
  canEdit: boolean
  busy: boolean
  onChange: (next: ScenePlatform) => void
  onApply: () => void
}) {
  const { t } = useTranslation(["workspace"])
  const setDraft = (patch: Partial<ScenePlatform>) => {
    onChange({ ...draft, ...patch, sector_size: DEFAULT_SECTOR_SIZE })
  }
  const setSectorCount = (raw: string, key: "sectors_x" | "sectors_z") => {
    const trimmed = raw.trim()
    if (trimmed === "") return
    const value = Number(trimmed)
    if (!Number.isFinite(value)) return
    const whole = Math.trunc(value)
    const clamped = Math.min(SECTORS_MAX, Math.max(SECTORS_MIN, whole))
    setDraft({ [key]: clamped })
  }
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ui-text-muted">{t("locationsPage.scenePlatform.sectorsX")}</span>
        <Input
          type="number"
          min={SECTORS_MIN}
          max={SECTORS_MAX}
          disabled={!canEdit}
          value={draft.sectors_x}
          onChange={(event) => setSectorCount(event.target.value, "sectors_x")}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ui-text-muted">{t("locationsPage.scenePlatform.sectorsZ")}</span>
        <Input
          type="number"
          min={SECTORS_MIN}
          max={SECTORS_MAX}
          disabled={!canEdit}
          value={draft.sectors_z}
          onChange={(event) => setSectorCount(event.target.value, "sectors_z")}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-ui-text-muted">{t("locationsPage.scenePlatform.unit")}</span>
        <select
          className="h-8 w-full rounded-lg border border-ui-border bg-ui-surface-sunken px-2 text-sm text-ui-text-primary"
          disabled={!canEdit}
          value={draft.unit}
          onChange={(event) => setDraft({ unit: event.target.value as PlatformUnit })}
        >
          <option value="cm">{t("locationsPage.scenePlatform.unitCm")}</option>
          <option value="m">{t("locationsPage.scenePlatform.unitM")}</option>
        </select>
      </label>
      {canEdit ? (
        <Button type="button" size="sm" disabled={busy} onClick={onApply}>
          {t("locationsPage.scenePlatform.apply")}
        </Button>
      ) : null}
    </div>
  )
}

function FacePanel({
  faces,
  faceKeys,
  prompt,
  canEdit,
  busy,
  modelLabel,
  delayEntityId,
  onPrompt,
  onGenerate,
}: {
  faces: SceneFaceState[]
  faceKeys: string[]
  prompt: string
  canEdit: boolean
  busy: boolean
  modelLabel?: string | null
  delayEntityId: number
  onPrompt: (value: string) => void
  onGenerate: () => Promise<void>
}) {
  const { t } = useTranslation(["workspace"])
  const generateActionRef = useRef<GenerateWithModelLabelHandle | null>(null)
  const single = faceKeys.length === 1
  const face = single ? (faces[0] ?? null) : null
  const hasImage = Boolean(face?.download_url || face?.url)
  const delayKey = single
    ? confirmDelayKey("location-scene-face", delayEntityId, faceKeys[0])
    : confirmDelayKey("location-scene-face", delayEntityId, "batch", [...faceKeys].sort().join(","))
  const pending = faces.some((item) => item.status === "pending")

  useEffect(() => {
    if (!canEdit) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return
      e.preventDefault()
      generateActionRef.current?.fireNow()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [canEdit])

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {single && !hasImage ? (
        <p className="text-xs text-ui-text-muted">{t("locationsPage.scenePlatform.faceEmpty")}</p>
      ) : null}
      {canEdit ? (
        <div className="relative min-w-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken">
          <Textarea
            value={prompt}
            rows={3}
            placeholder={t("locationsPage.scenePlatform.prompt")}
            onChange={(event) => onPrompt(event.target.value)}
            className="min-h-16 w-full min-w-0 border-0 bg-transparent pr-12 text-sm shadow-none focus-visible:ring-0"
          />
          <GenerateWithModelLabel
            label={t("locationsPage.scenePlatform.generate")}
            busyLabel={t("locationsPage.queuedShort")}
            modelLabel={modelLabel}
            iconOnly
            busy={pending}
            disabled={busy || prompt.trim() === ""}
            onClick={onGenerate}
            confirmDelayKey={delayKey}
            actionRef={generateActionRef}
            labelClassName="hidden"
            className="absolute right-1 bottom-1"
          />
        </div>
      ) : null}
    </div>
  )
}
